import React, { useEffect, useMemo, useState } from 'react'
import { Row, Col, Button, Form } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import CustomSelect from '../../../CustomReactSelectComponent'
import InputGroup from '../../../InputGroup'
import Switcher from '../../../CustomSwitchComponent'
import { CONTACT_FILTERS } from '../../../../constants/contactFilterCategory'
import { EXECUTIVE } from '../../../../constants/divisions'
import CustomNumber from '../../../CustomNumberFormat'
import useGetSupportTable from '../../../../hooks/useGetSupportTable'
import {
  COMPANY_TYPE,
  OPERATING_STATUS,
  EFFORT_LEVEL,
  INSURANCE_COMPANY,
  FACTORING_COMPANY,
  RED_FLAG_STATUS,
  PROSPECT_GRADE,
  OPERATION_CLASIFICATION,
  CARRIER_OPERATION,
  CARGO_CARRIED,
  AUTHORITY_TYPE,
} from '../../../../constants/supportTypes'
import useUsersForSelect from '../../../../hooks/useUsersForSelect'
import useDivisionsForSelect from '../../../../hooks/useDivisionsForSelect'
import useGetSalesperson from '../../../../hooks/useGetSalesperson'
import { BRANCH_MANAGER, MANAGER, VIEW_ALL_DIVISION_ACCOUNTS } from '../../../../constants/permissions'
import { distanceOptions, getDistanceRangeByValue } from '../../../../constants/dashboard'

const FilterInputs = ({
  selectedFilters,
  handleFiltersChanges,
  loadData,
  selectedFiltersValues,
  getOptions,
  options,
  setFiltersValue,
  removeFilter,
  allContactsOnly,
}) => {
  const [data, setData] = useState({})

  const { isExecutive, hasManagerPermission, hasViewAllDivisionAccountsPermission, divisionId } = useSelector(
    (state) => ({
      isExecutive: state.auth.user.division_id === EXECUTIVE,
      hasManagerPermission: [MANAGER, BRANCH_MANAGER].some((el) => state.auth.user.permissions.includes(el)),
      hasViewAllDivisionAccountsPermission: state.auth.user.permissions.includes(VIEW_ALL_DIVISION_ACCOUNTS),
      divisionId: state.auth.user.division_id,
    })
  )

  const salespersonDivision = useMemo(() => {
    if (divisionId !== 1) {
      return divisionId
    } else {
      return data?.salespersonDivisionId || 1
    }
  }, [divisionId, data?.salespersonDivisionId])

  const dispatch = useDispatch()

  const { options: salespersonOptions } = useGetSalesperson(salespersonDivision)
  const prospectGradeOptions = useGetSupportTable(PROSPECT_GRADE)
  const redFlagStatusOptions = useGetSupportTable(RED_FLAG_STATUS)
  const operatingStatusOptions = useGetSupportTable(OPERATING_STATUS)
  const companyTypeOptions = useGetSupportTable(COMPANY_TYPE)
  const effortLevelOptions = useGetSupportTable(EFFORT_LEVEL)
  const insuranceCompanyOptions = useGetSupportTable(INSURANCE_COMPANY)
  const factoringCompanyOptions = useGetSupportTable(FACTORING_COMPANY)
  const operationClasificationOpt = useGetSupportTable(OPERATION_CLASIFICATION)
  const carrierOperationOpt = useGetSupportTable(CARRIER_OPERATION)
  const cargoCarriedOpt = useGetSupportTable(CARGO_CARRIED)
  const authorityTypeOptions = useGetSupportTable(AUTHORITY_TYPE)

  const { data: userOptions, setDivisionIds } = useUsersForSelect()
  const divisionOptions = useDivisionsForSelect()
  const divisionAccountsOptions = hasViewAllDivisionAccountsPermission ? [...divisionOptions] : []

  const expandedOperatingStatusOptions = useMemo(() => {
    return [...(operatingStatusOptions ?? []), { id: null, name: 'No Status' }]
  }, [operatingStatusOptions])

  useEffect(() => {
    getOptions(CONTACT_FILTERS)
  }, [])

  const handleFilterDispatching = (filterName, filterValues, localFilterName = filterName) => {
    if (data[localFilterName]?.length > 0) {
      dispatch(setFiltersValue(filterName, filterValues))
    } else if (data[localFilterName]?.length === 0) {
      dispatch(removeFilter(filterName))
    }
  }

  useEffect(() => {
    handleFilterDispatching('company_type_id', data.companyTypeIds, 'companyTypeIds')
  }, [data.companyTypeIds])

  useEffect(() => {
    handleFilterDispatching('divisions.effort_level', data.effortLevel, 'effortLevel')
  }, [data.effortLevel])

  useEffect(() => {
    handleFilterDispatching('operating_status', data.operatingStatus, 'operatingStatus')
  }, [data.operatingStatus])

  useEffect(() => {
    handleFilterDispatching('factoring_company', data.factoringCompany, 'factoringCompany')
  }, [data.factoringCompany])

  useEffect(() => {
    handleFilterDispatching('insurance_company', data.insuranceCompany, 'insuranceCompany')
  }, [data.insuranceCompany])

  useEffect(() => {
    handleFilterDispatching('divisions.grade', data.grade, 'grade')
  }, [data.grade])

  useEffect(() => {
    const filterValues = mapStatusIds()
    handleFilterDispatching('crf.status_id', filterValues, 'redFlagStatusIds')
  }, [data.redFlagStatusIds])

  const mapStatusIds = () => {
    const { redFlagStatusIds } = data
    const mappedValues = []
    if (redFlagStatusIds?.length > 0) redFlagStatusIds.forEach((id) => mappedValues.push(id === 94 ? 2 : 1)) //94 Closed
    return mappedValues
  }

  useEffect(() => {
    if (data?.redFlagDivisionId?.length > 0) {
      setDivisionIds(data.redFlagDivisionId)
      dispatch(setFiltersValue('crf.division_id', data.redFlagDivisionId))
    } else if (data?.redFlagDivisionId?.length === 0) {
      setDivisionIds([])
      dispatch(removeFilter('crf.division_id'))
    }

    //remove red flag users on division change
    setData((prevState) => {
      // eslint-disable-next-line no-unused-vars
      const { redFlagCreatedBy, ...otherFilters } = prevState
      return {
        ...otherFilters,
      }
    })

    dispatch(removeFilter('crf.created_by'))
  }, [data.redFlagDivisionId])

  useEffect(() => {
    handleFilterDispatching('crf.created_by', data.redFlagCreatedBy, 'redFlagCreatedBy')
  }, [data.redFlagCreatedBy])

  useEffect(() => {
    if (data?.salesperson?.length > 0) {
      dispatch(setFiltersValue('divisions.assigned', data.salesperson))
    } else if (data?.salesperson?.length === 0) {
      dispatch(removeFilter('divisions.assigned'))
    }
  }, [data.salesperson])

  useEffect(() => {
    if (data?.divisionLeads) {
      dispatch(setFiltersValue('divisionLeads', data.divisionLeads))
    } else {
      dispatch(removeFilter('divisionLeads'))
    }
  }, [data.divisionLeads])

  useEffect(() => {
    if (data['divisionAccounts.divisions']?.length > 0) {
      dispatch(
        setFiltersValue('divisionAccounts', {
          divisions: data['divisionAccounts.divisions'],
          exact: data['divisionAccounts.exact'] || false,
        })
      )
    } else if (data['divisionAccounts.divisions']?.length === 0) {
      dispatch(removeFilter('divisionAccounts'))
    }
  }, [data['divisionAccounts.divisions'], data['divisionAccounts.exact']])

  useEffect(() => {
    handleFilterDispatching('organization_type_id', data.organizationTypes, 'organizationTypes')
  }, [data.organizationTypes])

  useEffect(() => {
    handleFilterDispatching('source_id', data.sourceIds, 'sourceIds')
  }, [data.sourceIds])

  useEffect(() => {
    handleFilterDispatching('nationality_id', data.nationalities, 'nationalities')
  }, [data.nationalities])

  useEffect(() => {
    handleFilterDispatching('address_state', data.states, 'states')
  }, [data.states])

  useEffect(() => {
    handleFilterDispatching('created_by', data.createdBy, 'createdBy')
  }, [data.createdBy])

  useEffect(() => {
    const value = getDistanceRangeByValue(data.distance)

    if (data.distance) {
      dispatch(setFiltersValue('distance', value))
    } else {
      dispatch(removeFilter('distance'))
    }
  }, [data.distance])

  useEffect(() => {
    handleFilterDispatching('operation_clasification', data.operationClasification, 'operationClasification')
  }, [data.operationClasification])

  useEffect(() => {
    handleFilterDispatching('carrier_operation', data.carrierOperation, 'carrierOperation')
  }, [data.carrierOperation])

  useEffect(() => {
    handleFilterDispatching('cargo_carried', data.cargoCarried, 'cargoCarried')
  }, [data.cargoCarried])

  useEffect(() => {
    handleFilterDispatching('authority_type', data.authorityType, 'authorityType')
  }, [data.authorityType])

  const handleSelectChange = (values, e, isMulti) => {
    const { name } = e

    if (isMulti) {
      if (values) {
        const newValues = []
        values.forEach((value) => {
          newValues.push(value.id)
        })
        setData({ ...data, [name]: newValues })
      } else {
        setData({ ...data, [name]: [] })
      }
    } else {
      setData({ ...data, [name]: values ? values.value || values.id : null })
    }
  }

  const handleSwitchChange = (e) => {
    const { name, value } = e.target

    setData((prev) => ({ ...prev, [name]: value }))
  }

  const validateRangeFilter = (event) => {
    if (event.target.value > 100) event.target.value = 100
    if (event.target.value < 0) event.target.value = 0
  }

  return (
    <>
      <Form noValidate onSubmit={() => loadData()}>
        <Row className='mt-3 filterInputs'>
          {selectedFilters.name && (
            <Col xs={3}>
              <InputGroup
                label={'Name'}
                value={selectedFiltersValues.name}
                onChange={(event) => handleFiltersChanges(event)}
                type={'text'}
                placeholder={''}
                name={'name'}
              />
            </Col>
          )}
          {selectedFilters.email && (
            <Col xs={3}>
              <InputGroup
                label={'Email'}
                onChange={(event) => handleFiltersChanges(event)}
                type={'email'}
                placeholder={''}
                name={'email'}
              />
            </Col>
          )}
          {selectedFilters.operating_status && (
            <Col xs={3}>
              <CustomSelect
                label={'Operating Status'}
                name='operatingStatus'
                isMulti
                isClearable
                options={expandedOperatingStatusOptions || []}
                defaultValue={data?.operatingStatus || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}
          {selectedFilters.effort_level && (
            <Col xs={3}>
              <CustomSelect
                label={'Effort Level'}
                name={'effortLevel'}
                isMulti
                isClearable
                options={effortLevelOptions || []}
                defaultValue={data?.effortLevel || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}

          {selectedFilters?.division_accounts && hasViewAllDivisionAccountsPermission && (
            <>
              <Col xs={3}>
                <CustomSelect
                  label={'Division Accounts'}
                  name={'divisionAccounts.divisions'}
                  isMulti
                  isClearable
                  defaultValue={data['divisionAccounts.divisions'] || []}
                  options={divisionAccountsOptions || []}
                  onChange={(values, event) => handleSelectChange(values, event, true)}
                />
              </Col>
              {data['divisionAccounts.divisions']?.length > 1 && (
                <Col xs={3}>
                  <Switcher
                    name={'divisionAccounts.exact'}
                    labelName={'Account In All Selected Divisions'}
                    onChange={(event) => handleSwitchChange(event)}
                    checked={Boolean(data['divisionAccounts.exact'])}
                  />
                </Col>
              )}
            </>
          )}

          {selectedFilters.company_type_id && (
            <Col xs={3}>
              <CustomSelect
                label={'Company Type'}
                name={'companyTypeIds'}
                options={companyTypeOptions || []}
                isMulti
                isClearable
                defaultValue={data.companyTypeIds || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}
          {selectedFilters.last_contact_date && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Last Contact From'}
                  type={'date'}
                  placeholder={''}
                  name={'last_contact_date'}
                  value={selectedFiltersValues.last_contact_date?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Last Contact To'}
                  type={'date'}
                  placeholder={''}
                  name={'last_contact_date'}
                  value={selectedFiltersValues.last_contact_date?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}

          {selectedFilters.last_safer_update && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Last Safer Update From'}
                  type={'date'}
                  placeholder={''}
                  name={'last_safer_update'}
                  value={selectedFiltersValues.last_safer_update?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Last Safer Update To'}
                  type={'date'}
                  placeholder={''}
                  name={'last_safer_update'}
                  value={selectedFiltersValues.last_safer_update?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}

          {selectedFilters.insurance_company && (
            <Col xs={3}>
              <CustomSelect
                label={'Insurance Company'}
                name={'insuranceCompany'}
                isMulti
                isClearable
                options={insuranceCompanyOptions || []}
                defaultValue={data?.insuranceCompany || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}

          {selectedFilters?.exp_date && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Insurance Expiration From'}
                  name={'exp_date'}
                  type={'date'}
                  placeholder={''}
                  // onChange={() => {}}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Insurance Expiration To'}
                  name={'exp_date'}
                  type={'date'}
                  placeholder={''}
                  // onChange={() => {}}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}
          {selectedFilters?.grade && (
            <Col xs={3}>
              <CustomSelect
                label={'Grade'}
                name={'grade'}
                isMulti
                isClearable
                options={prospectGradeOptions}
                defaultValue={data?.grade || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}
          {selectedFilters?.red_flag && (
            <>
              <Col xs={3}>
                <CustomSelect
                  label={'Red Flag'}
                  name={'redFlagStatusIds'}
                  isMulti
                  isClearable
                  defaultValue={data?.redFlagStatusIds || []}
                  options={redFlagStatusOptions || []}
                  onChange={(values, event) => handleSelectChange(values, event, true)}
                />
              </Col>
              <Col xs={4}>
                <CustomSelect
                  label={'Red Flag User'}
                  name={'redFlagCreatedBy'}
                  isMulti
                  isClearable
                  defaultValue={data.redFlagCreatedBy || []}
                  options={userOptions || []}
                  onChange={(values, event) => handleSelectChange(values, event, true)}
                />
              </Col>
              <Col xs={4}>
                <CustomSelect
                  label={'Red Flag Division'}
                  name={'redFlagDivisionId'}
                  isMulti
                  isClearable
                  defaultValue={data?.redFlagDivisionId || []}
                  options={divisionOptions || []}
                  onChange={(values, event) => handleSelectChange(values, event, true)}
                />
              </Col>
              <Col xs={3}>
                <InputGroup
                  label={'Red Flag Date (min)'}
                  name={'crf.creation_date'}
                  type={'date'}
                  placeholder={''}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={3}>
                <InputGroup
                  label={'Red Flag Date (max)'}
                  name={'crf.creation_date'}
                  type={'date'}
                  placeholder={''}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}
          {selectedFilters?.address_city && (
            <Col xs={3}>
              <InputGroup
                label={'City'}
                name={'address_city'}
                type={'text'}
                placeholder={''}
                value={selectedFiltersValues?.address_city}
                onChange={(event) => handleFiltersChanges(event)}
              />
            </Col>
          )}
          {selectedFilters.factoring_company && (
            <Col xs={3}>
              <CustomSelect
                label={'Factoring Company'}
                name={'factoringCompany'}
                isMulti
                isClearable
                options={factoringCompanyOptions || []}
                defaultValue={data?.factoringCompany || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}
          {selectedFilters.mc_number && (
            <Col xs={3}>
              <InputGroup
                label='mc number'
                type='number'
                placeholder={''}
                name='mc_number'
                onChange={(event) => handleFiltersChanges(event)}
                value={selectedFiltersValues.mc_number}
              />
            </Col>
          )}
          {selectedFilters.owner_name && (
            <Col xs={3}>
              <InputGroup
                label='Company Owner'
                type='text'
                name='owner_name'
                placeholder={''}
                onChange={(event) => handleFiltersChanges(event)}
                value={selectedFiltersValues.owner_name}
              />
            </Col>
          )}
          {selectedFilters.business_phone && (
            <Col xs={3}>
              <CustomNumber
                className={'form-control-sm form-control'}
                label={'Business Phone'}
                name={'business_phone'}
                value={selectedFiltersValues?.business_phone}
                placeholder={'Business Phone'}
                type='tel'
                format='(999) 999-9999'
                onChange={(event) => handleFiltersChanges(event)}
                size={10}
              />
            </Col>
          )}
          {selectedFilters.dot_number && (
            <Col xs={3}>
              <InputGroup
                label='DOT number'
                name='dot_number'
                onChange={(event) => handleFiltersChanges(event)}
                value={selectedFiltersValues?.dot_number}
              />
            </Col>
          )}
          {selectedFilters?.nationality_id && (
            <Col xs={3}>
              <CustomSelect
                label='Preferred Language'
                name={'nationalities'}
                isMulti
                isClearable
                options={options.Nationality || []}
                defaultValue={data?.nationalities || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}
          {selectedFilters.number_of_trucks && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='No. of trucks from'
                  name='number_of_trucks'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                  value={selectedFiltersValues?.number_of_trucks?.from}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='No. of trucks to'
                  name='number_of_trucks'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                  value={selectedFiltersValues?.number_of_trucks?.to}
                />
              </Col>
            </>
          )}
          {selectedFilters.number_of_trailers && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='No. of trailers from'
                  name='number_of_trailers'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                  value={selectedFiltersValues?.number_of_trailers?.from}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='No. of trailers to'
                  name='number_of_trailers'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                  value={selectedFiltersValues?.number_of_trailers?.to}
                />
              </Col>
            </>
          )}
          {selectedFilters.number_of_drivers && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='No. of drivers from'
                  name='number_of_drivers'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                  value={selectedFiltersValues?.number_of_drivers?.from}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='No. of drivers to'
                  name='number_of_drivers'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                  value={selectedFiltersValues?.number_of_drivers?.to}
                />
              </Col>
            </>
          )}
          {selectedFilters.estimated_revenue && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='Est. revenue from'
                  name='estimated_revenue'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                  value={selectedFiltersValues?.estimated_revenue?.from}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='Est. revenue to'
                  name='estimated_revenue'
                  type='number'
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                  value={selectedFiltersValues?.estimated_revenue?.to}
                />
              </Col>
            </>
          )}
          {selectedFilters.dba && (
            <Col xs={3}>
              <InputGroup
                label='DBA'
                name='dba'
                onChange={(event) => handleFiltersChanges(event)}
                value={selectedFiltersValues.dba}
              />
            </Col>
          )}
          {selectedFilters.address_zip && (
            <Col xs={3}>
              <InputGroup
                label='Zip'
                name='address_zip'
                onChange={(event) => handleFiltersChanges(event)}
                value={selectedFiltersValues.address_zip}
              />
            </Col>
          )}
          {selectedFilters.address_state && (
            <Col xs={3}>
              <CustomSelect
                label='State'
                name={'states'}
                isMulti
                isClearable
                defaultValue={data.states || []}
                options={options.States || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}
          {selectedFilters.created_by && (
            <Col xs={3}>
              <CustomSelect
                label='Created By'
                name={'createdBy'}
                isMulti
                isClearable
                options={options.Users || []}
                defaultValue={data.createdBy || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}

          {selectedFilters.organization_type_id && (
            <Col xs={3}>
              <CustomSelect
                label='Organization Type'
                name={'organizationTypes'}
                isMulti
                isClearable
                options={options.OrganizationType || []}
                defaultValue={data.organizationTypes || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
              />
            </Col>
          )}

          {selectedFilters?.source_id && (
            <Col xs={3}>
              <CustomSelect
                label='Source'
                name={'sourceIds'}
                isMulti
                isClearable
                defaultValue={data?.sourceIds || []}
                onChange={(values, event) => handleSelectChange(values, event, true)}
                options={options.SourceType || []}
              />
            </Col>
          )}

          {selectedFilters?.date_established && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='Date Established from'
                  name='date_established'
                  type='date'
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                  value={selectedFiltersValues?.date_established?.from}
                  placeholder={''}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='Date Established to'
                  name='date_established'
                  type='date'
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                  value={selectedFiltersValues?.date_established?.to}
                  placeholder={''}
                />
              </Col>
            </>
          )}

          {selectedFilters?.prospect_date && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Prospect Date From'}
                  name={'csc.creation_date'}
                  type={'date'}
                  placeholder={''}
                  value={selectedFilters?.csc?.creation_date?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Prospect Date To'}
                  name={'csc.creation_date'}
                  type={'date'}
                  placeholder={''}
                  value={selectedFilters?.csc?.creation_date?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}

          {selectedFilters?.account_date && allContactsOnly && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Account Date From'}
                  name={'divisions.account_date'}
                  type={'date'}
                  value={selectedFiltersValues['divisions.account_date']?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Account Date To'}
                  name={'divisions.account_date'}
                  type={'date'}
                  value={selectedFiltersValues['divisions.account_date']?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}

          {selectedFilters.import_date && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Import Date from'}
                  name={'import_date'}
                  type={'date'}
                  placeholder={''}
                  value={selectedFiltersValues?.import_date?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Import Date to'}
                  type={'date'}
                  placeholder={''}
                  name={'import_date'}
                  value={selectedFiltersValues?.import_date?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}
          {selectedFilters.creation_date && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'Date Entered from'}
                  name={'creation_date'}
                  type={'date'}
                  placeholder={''}
                  value={selectedFiltersValues?.creation_date?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'Date Entered to '}
                  name={'creation_date'}
                  placeholder={''}
                  type={'date'}
                  value={selectedFiltersValues?.creation_date?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}
          {selectedFilters?.ucc_eff_date && (
            <>
              <Col xs={2}>
                <InputGroup
                  label={'UCC Date from'}
                  name={'ucc_eff_date'}
                  type={'date'}
                  placeholder={''}
                  value={selectedFiltersValues?.ucc_eff_date?.from}
                  onChange={(event) => handleFiltersChanges(event, 'from')}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label={'UCC Date to'}
                  name={'ucc_eff_date'}
                  placeholder={''}
                  type={'date'}
                  value={selectedFiltersValues?.ucc_eff_date?.to}
                  onChange={(event) => handleFiltersChanges(event, 'to')}
                />
              </Col>
            </>
          )}

          {selectedFilters?.salesperson && hasManagerPermission && (
            <>
              {isExecutive && allContactsOnly && (
                <Col xs={4}>
                  <CustomSelect
                    disabled={!isExecutive}
                    label={'Salespersons In'}
                    name={'salespersonDivisionId'}
                    isClearable
                    defaultValue={data?.salespersonDivisionId || null}
                    options={divisionOptions || []}
                    onChange={(values, event) => handleSelectChange(values, event)}
                  />
                </Col>
              )}

              <Col xs={4}>
                <CustomSelect
                  label={'Salesperson'}
                  name={'salesperson'}
                  isMulti
                  isClearable
                  defaultValue={data?.salesperson || []}
                  options={salespersonOptions || []}
                  onChange={(values, event) => handleSelectChange(values, event, true)}
                />
              </Col>
            </>
          )}

          {selectedFilters.is_ex_yu && isExecutive && (
            <Col xs={3}>
              <Switcher
                name={'is_ex_yu'}
                labelName={'Ex-Yu'}
                onChange={(event) => handleFiltersChanges(event)}
                checked={!!selectedFiltersValues.is_ex_yu}
              />
            </Col>
          )}

          {selectedFilters.division_leads && isExecutive && (
            <Col xs={4}>
              <CustomSelect
                disabled={!isExecutive}
                label={'Division Leads'}
                name={'divisionLeads'}
                isClearable
                defaultValue={data?.divisionLeads || null}
                options={divisionOptions || []}
                onChange={(values, event) => handleSelectChange(values, event)}
              />
            </Col>
          )}

          {selectedFilters.distance && (
            <Col xs={4}>
              <CustomSelect
                label={'Distance'}
                placeholder={'Please Select...'}
                name={'distance'}
                options={distanceOptions || []}
                isClearable
                onChange={(values, event) => handleSelectChange(values, event)}
                defaultValue={data.distance || null}
                disabled={!selectedFiltersValues?.address_zip}
              />
            </Col>
          )}

          {selectedFilters.operation_classification && (
            <Col xs={3}>
              <CustomSelect
                label={'Operation Clasification'}
                placeholder={'Please Select...'}
                name='operationClasification'
                options={operationClasificationOpt}
                isClearable
                onChange={(values, event) => handleSelectChange(values, event, true)}
                defaultValue={data.operationClasification || []}
                isMulti
              />
            </Col>
          )}

          {selectedFilters.carrier_operation && (
            <Col xs={3}>
              <CustomSelect
                label={'Carrier Operation'}
                placeholder={'Please Select...'}
                name='carrierOperation'
                options={carrierOperationOpt}
                isClearable
                onChange={(values, event) => handleSelectChange(values, event, true)}
                defaultValue={data.carrierOperation || []}
                isMulti
              />
            </Col>
          )}

          {selectedFilters.cargo_carried && (
            <Col xs={3}>
              <CustomSelect
                label={'Cargo Carried'}
                placeholder={'Please Select...'}
                name='cargoCarried'
                options={cargoCarriedOpt}
                isClearable
                onChange={(values, event) => handleSelectChange(values, event, true)}
                defaultValue={data.cargoCarried || []}
                isMulti
              />
            </Col>
          )}

          {selectedFilters.chameleon_type1 && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='Chameleon Type 1 From'
                  type='number'
                  max={100}
                  min={0}
                  name='chameleon_type1'
                  value={selectedFiltersValues.chameleon_type1?.from}
                  onChange={(event) => {
                    validateRangeFilter(event)
                    handleFiltersChanges(event, 'from')
                  }}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='Chameleon Type 1 To'
                  type='number'
                  max={100}
                  min={0}
                  name='chameleon_type1'
                  value={selectedFiltersValues.chameleon_type1?.to}
                  onChange={(event) => {
                    validateRangeFilter(event)
                    handleFiltersChanges(event, 'to')
                  }}
                />
              </Col>
            </>
          )}

          {selectedFilters.chameleon_type2 && (
            <>
              <Col xs={2}>
                <InputGroup
                  label='Chameleon Type 2 From'
                  type='number'
                  max={100}
                  min={0}
                  name='chameleon_type2'
                  value={selectedFiltersValues.chameleon_type2?.from}
                  onChange={(event) => {
                    validateRangeFilter(event)
                    handleFiltersChanges(event, 'from')
                  }}
                />
              </Col>
              <Col xs={2}>
                <InputGroup
                  label='Chameleon Type 2 To'
                  type='number'
                  max={100}
                  min={0}
                  name='chameleon_type2'
                  value={selectedFiltersValues.chameleon_type2?.to}
                  onChange={(event) => {
                    validateRangeFilter(event)
                    handleFiltersChanges(event, 'to')
                  }}
                />
              </Col>
            </>
          )}

          {selectedFilters.authority_type && (
            <Col xs={3}>
              <CustomSelect
                label={'Authority Type'}
                placeholder={'Please Select...'}
                name='authorityType'
                options={authorityTypeOptions}
                isClearable
                onChange={(values, event) => handleSelectChange(values, event, true)}
                defaultValue={data.authorityType || []}
                isMulti
              />
            </Col>
          )}
        </Row>
      </Form>
      <Row className='flex-row-reverse mr-3'>
        <Button
          className={'btn btn-gold'}
          type='submit'
          onClick={() => {
            loadData()
          }}
        >
          Search
        </Button>
      </Row>
    </>
  )
}

FilterInputs.propTypes = {
  selectedFilters: PropTypes.object.isRequired,
  selectedFiltersValues: PropTypes.object,
  handleFiltersChanges: PropTypes.object.isRequired,
  loadData: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  getOptions: PropTypes.func.isRequired,
}

FilterInputs.defaultProps = {
  selectedFiltersValues: {},
}

export default FilterInputs
